<template>
    <v-expansion-panel class="SigningChecklistAddStep2File-expansionPanel" data-locator="file-expansion-panel" :data-name="file.name">
      <v-expansion-panel-title>
        <SigningChecklistDocumentHeader
          :file="file"
          :validator="v$"
          @delete-file="isDeleteFileDialogOpened = true"
        />
      </v-expansion-panel-title>
      <v-expansion-panel-text class="py-6">
        <SigningChecklistSection data-locator="informations-section">
          <template #title>
            1. {{ $t(`${TRANSLATION_KEY_PREFIX}.sectionTitles.documentInformations`) }}
          </template>
          <template #content>
            <Section1
              :file-id="file.id || file.originalFile.id"
              :file-date="file.objectToPost.date"
              :is-editing-limited="isEditingLimited"
              :title-form-field="v$['fileFormField'].title"
            />
          </template>
        </SigningChecklistSection>

        <SigningChecklistSection data-locator="signature-type-section">
          <template #title>
            2. {{ $t(`${TRANSLATION_KEY_PREFIX}.sectionTitles.signatureType`) }}
          </template>
          <template #content>
            <Section2
                :envelope-type="file.objectToPost.envelopeType"
                :file-id="file.id"
                :file-size="file.size"
                :has-mercury-option-activated="hasMercuryOptionActivated"
                :hasSignersOrder="hasSignersOrder"
                :is-editing-limited="isEditingLimited"
                :signature-type-form-field="v$['fileFormField'].signatureType"
                @setQualifiedDSWarningDialog="qualifiedDSWarningDialog = $event"
                @setSelectSignatureType="selectSignatureType"
            />
          </template>
        </SigningChecklistSection>

        <SigningChecklistSection data-locator="envelope-type-section">
          <template #title>
            3. {{ $t(`${TRANSLATION_KEY_PREFIX}.sectionTitles.envelopeType`) }}
          </template>
          <template #content>
            <Section3 :file-id="file.id || file.originalFile.id"
                      :envelope-type-form-field="v$['fileFormField'].envelopeType"
                      :is-editing-limited="isEditingLimited"
                      :isMultiEnvelopeChild="isMultiEnvelopeChild"
                      :signature-type="file.objectToPost.signatureType"
            />
          </template>
        </SigningChecklistSection>

        <SigningChecklistSection data-locator="roles-section">
          <template #title>
            4. {{ $t(`${TRANSLATION_KEY_PREFIX}.sectionTitles.roles`) }}
          </template>
          <template #content>
            <Section4
              :file="file"
              :has-signers-order="hasSignersOrder"
              :is-editing-limited="isEditingLimited"
              @resetSigners="resetSigners"
            />
          </template>
        </SigningChecklistSection>

        <SigningChecklistSection v-if="addFromTemplate">
          <template #title>
            5. {{ $t(`${TRANSLATION_KEY_PREFIX}.sectionTitles.signersAssignation`) }}
          </template>

          <template #content>
            <SigningChecklistSignersAssignationList
              :signers="fileSigners"
              :envelope-type="file.objectToPost.envelopeType"
              :template-signatures="file.templateSignatures"
              @assign-signer="assignGenericSigner"
            />
          </template>
        </SigningChecklistSection>
      </v-expansion-panel-text>

      <SigningChecklistAddStep2DeleteStepWarningDialog v-if="isDeleteFileDialogOpened"
                                                       @close="isDeleteFileDialogOpened = false"
                                                       @confirm="deleteFile"
      />

      <QualifiedDSSignatureWarningDialog v-if="qualifiedDSWarningDialog"
                                         @close="qualifiedDSWarningDialog = false"
                                         @confirm="confirmQualifiedSignatureSelection"
      />
    </v-expansion-panel>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { intersectionWith } from 'lodash-es'
import { defineComponent, inject, PropType } from 'vue'
import { createNamespacedHelpers, mapGetters } from 'vuex'

import { toast } from '@/common/utils/toast'
import SigningChecklistDocumentHeader from '@/project/signing-checklist/add/SigningChecklistDocumentHeader.vue'
import SigningChecklistSection from '@/project/signing-checklist/add/SigningChecklistSection.vue'
import { SIGNATURE_TYPE_HANDWRITTEN } from '@/project/signing-checklist/constants'
import QualifiedDSSignatureWarningDialog from '@/project/signing-checklist/dialogs/QualifiedDSSignatureWarningDialog.vue'
import { signingChecklistAddInjectionKey, SigningChecklistAddProvidedData } from '@/project/views/inject-keys/signingChecklistAddInjectionKey'
import { GenericSignature, SigningChecklistFile } from '@/project/views/SigningChecklistAdd.vue'
import {
  ASSIGN_SIGNER_ON_GENERIC_SIGNATURE,
  REMOVE_IMPORTED_FILE,
  SET_IMPORTED_FILE_SIGNATURE_TYPE,
} from '@/store/modules/signing-checklist/mutation_types'

import Section1 from './Section1.vue'
import Section2 from './Section2.vue'
import Section3 from './Section3.vue'
import Section4 from './Section4.vue'
import SigningChecklistSignersAssignationList from './signers-assignation/SigningChecklistSignersAssignationList.vue'
import SigningChecklistAddStep2DeleteStepWarningDialog from './SigningChecklistAddStep2DeleteStepWarningDialog.vue'

const { mapState: mapSigningChecklistState, mapMutations: mapSigningChecklistMutations } = createNamespacedHelpers('signingChecklist')
const TRANSLATION_KEY_PREFIX = 'project.signing-checklist.add.step2.SigningChecklistAddStep2File'

export default defineComponent({
  name: 'SigningChecklistAddStep2File',
  components: {
    QualifiedDSSignatureWarningDialog,
    Section1,
    Section2,
    Section3,
    Section4,
    SigningChecklistAddStep2DeleteStepWarningDialog,
    SigningChecklistDocumentHeader,
    SigningChecklistSection,
    SigningChecklistSignersAssignationList,
  },
  props: {
    file: {
      type: Object as PropType<SigningChecklistFile>,
      required: true,
    },
  },
  setup: () => {
    const signingChecklistAddProvidedData = inject<SigningChecklistAddProvidedData>(signingChecklistAddInjectionKey)

    return {
      addFromTemplate: signingChecklistAddProvidedData?.addFromTemplate,
      v$: useVuelidate(),
    }
  },
  validations () {
    return {
      fileFormField: {
        envelopeType: { required },
        signatureType: { required },
        title: { required },
      },
    }
  },
  data () {
    return {
      isDeleteFileDialogOpened: false,
      qualifiedDSWarningDialog: false,
      SIGNATURE_TYPE_HANDWRITTEN,
      TRANSLATION_KEY_PREFIX,
    }
  },
  computed: {
    ...mapGetters('groups', ['allRoomMembers']),
    ...mapSigningChecklistState(['importedFiles']),
    hasSignersOrder () {
      return this.file?.objectToPost?.signers?.length > 1
    },
    isMultiEnvelopeChild (){
      return !!this.file.objectToPost.envelope?.parentMultienvelopeId
    },
    // If an envelope is in process, we will not be able to change much info about it, it's too late
    isEditingLimited () {
      return (this.file.objectToPost?.envelope?.status === 'signed' ||
        this.file.objectToPost?.envelope?.status === 'sent' ||
        this.file.objectToPost?.envelope?.status === 'voided' ||
        this.file.objectToPost?.envelope?.status === 'unvalidated'
      )
    },
    hasMercuryOptionActivated () {
      return !!this.file.providerOptions?.['mercury']
    },
    fileSigners () {
      return intersectionWith(
        this.allRoomMembers,
        this.file.objectToPost.signers.flat(),
        (roomMember: ObjectWith<{ id: number, fullName: string }>, signerId: number) => roomMember.id === signerId,
      ).map(member => ({
        id: member.id,
        name: member.fullName,
      }))
    },
    fileFormField () {
      const { envelopeType, signatureType, title } = this.file.objectToPost
      const { templateSignatures } = this.file

      return {
        envelopeType,
        signatureType,
        templateSignatures,
        title,
      }
    },
  },
  methods: {
    ...mapSigningChecklistMutations([
      ASSIGN_SIGNER_ON_GENERIC_SIGNATURE,
      REMOVE_IMPORTED_FILE,
      SET_IMPORTED_FILE_SIGNATURE_TYPE,
    ]),
    deleteFile (file: SigningChecklistFile) {
      this.REMOVE_IMPORTED_FILE(this.importedFiles.findIndex((f: SigningChecklistFile) => f === file))
    },
    confirmQualifiedSignatureSelection () {
      this.qualifiedDSWarningDialog = false
      this.selectSignatureType('ds.qualified')
    },
    selectSignatureType (signatureType: string) {
      this.SET_IMPORTED_FILE_SIGNATURE_TYPE({
        fileId: this.file.id,
        signatureType: signatureType,
      })

      // reset signature order if user selects handwritten signature
      if (signatureType === SIGNATURE_TYPE_HANDWRITTEN) {
        if (this.hasSignersOrder) {
          toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.signatoriesOrderRemovedSuccess`))
        }
        this.resetSigners()
      }

      this.$emit('signatureTypeChange', this.file, signatureType)
    },
    resetSigners () {
      this.file.objectToPost.signers = [this.fileSigners]
    },
    assignGenericSigner (signatureId: GenericSignature['id'], signer: EnvelopeSigner) {
      this.ASSIGN_SIGNER_ON_GENERIC_SIGNATURE({
        fileId: this.file.id,
        signatureId: signatureId,
        signer: signer,
      })
    },
  },
})
</script>

<style scoped lang="scss">
.SigningChecklistAddStep2File-expansionPanel {
  :deep(.v-expansion-panel-title) {
    border-bottom: 1px solid #DEE2E6;
    border-radius: 6px !important;
  }
  :deep(.v-expansion-panel-text__wrapper) {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
  }
}
</style>
